import React, { useRef, useState, useEffect } from "react";
import { Pen, Upload, Trash2, Check, Loader2, RefreshCw } from "lucide-react";

/**
 * SignatureCapture
 * ------------------
 * Lets a named signer either draw a signature on a canvas, or upload
 * an image file of their signature, then saves it to the Laravel backend.
 *
 * Props:
 *  - signerName: string   e.g. "Jamie" or "Neil"   (required)
 *  - signerId:   string   a stable identifier used as the DB key,
 *                         e.g. "jamie" or "neil"   (required)
 *  - apiBaseUrl: string   defaults to REACT_APP_API_BASE_URL from your .env
 *  - onSaved:    fn(signatureRecord)  called after a successful save
 */
const SignatureCapture = ({
  signerName,
  signerId,
  apiBaseUrl = process.env.REACT_APP_API_BASE_URL,
  onSaved,
}) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const isDrawing = useRef(false);
  const hasDrawn = useRef(false);

  const [mode, setMode] = useState("draw"); // "draw" | "upload"
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [existingSignature, setExistingSignature] = useState(null);
  const [loadingExisting, setLoadingExisting] = useState(true);

  // Fetch the most recent saved signature for this signer, if any
  useEffect(() => {
    let cancelled = false;
    const fetchExisting = async () => {
      try {
        const res = await fetch(`${apiBaseUrl}/signatures/${signerId}`, {
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
          },
        });
        if (res.ok) {
          const data = await res.json();
          
          if (!cancelled) setExistingSignature(data.data || null);
        }
      } catch (e) {
        // silently ignore - just means no signature yet or endpoint offline
      } finally {
        if (!cancelled) setLoadingExisting(false);
      }
    };
    fetchExisting();
    return () => {
      cancelled = true;
    };
  }, [signerId, apiBaseUrl]);

  // Set up the canvas (handles device pixel ratio so lines are crisp)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const resize = () => {
      const ratio = window.devicePixelRatio || 1;
      const rect = canvas.parentElement.getBoundingClientRect();
      canvas.width = rect.width * ratio;
      canvas.height = 180 * ratio;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `180px`;
      const ctx = canvas.getContext("2d");
      ctx.scale(ratio, ratio);
      ctx.lineWidth = 2.2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = "#1e293b";
    };
    resize();
    window.addEventListener("resize", resize);
    return () => window.removeEventListener("resize", resize);
  }, [mode]);

  const getPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const point = e.touches ? e.touches[0] : e;
    return {
      x: point.clientX - rect.left,
      y: point.clientY - rect.top,
    };
  };

  const startDraw = (e) => {
    e.preventDefault();
    isDrawing.current = true;
    hasDrawn.current = true;
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const draw = (e) => {
    if (!isDrawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const { x, y } = getPos(e);
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDraw = () => {
    isDrawing.current = false;
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    hasDrawn.current = false;
    setError(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      setError("Please choose an image file (PNG or JPG).");
      return;
    }
    setError(null);
    setUploadFile(file);
    const reader = new FileReader();
    reader.onload = () => setUploadPreview(reader.result);
    reader.readAsDataURL(file);
  };

  const handleSave = async () => {
    setError(null);

    if (mode === "draw" && !hasDrawn.current) {
      setError("Please draw a signature before saving.");
      return;
    }
    if (mode === "upload" && !uploadFile) {
      setError("Please choose a signature image before saving.");
      return;
    }

    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("signer_id", signerId);
      formData.append("signer_name", signerName);
      formData.append("type", mode);

      if (mode === "draw") {
        const dataUrl = canvasRef.current.toDataURL("image/png");
        const blob = await (await fetch(dataUrl)).blob();
        formData.append("signature_file", blob, `${signerId}-signature.png`);
      } else {
        formData.append("signature_file", uploadFile);
      }

      const res = await fetch(`${apiBaseUrl}/signatures`, {
        method: "POST",
        headers: {
          // Note: no Content-Type here — the browser sets the correct
          // multipart/form-data boundary automatically for FormData bodies.
          Accept: "application/json",
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
        body: formData,
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.message || "Failed to save signature.");
      }

      const data = await res.json();
      
      setExistingSignature(data.data);
      clearCanvas();
      setUploadFile(null);
      setUploadPreview(null);
      if (onSaved) onSaved(data.data);
    } catch (e) {
      setError(e.message || "Something went wrong saving the signature.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      ref={containerRef}
      className="bg-white p-6 rounded-xl shadow-sm border border-gray-200"
    >
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">
          {signerName}'s Signature
        </h3>

        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            type="button"
            onClick={() => setMode("draw")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              mode === "draw"
                ? "bg-white shadow-sm text-blue-700"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Pen className="h-4 w-4" />
            Draw
          </button>
          <button
            type="button"
            onClick={() => setMode("upload")}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              mode === "upload"
                ? "bg-white shadow-sm text-blue-700"
                : "text-gray-500 hover:text-gray-700"
            }`}
          >
            <Upload className="h-4 w-4" />
            Upload
          </button>
        </div>
      </div>

      {/* Existing signature on file */}
      {!loadingExisting && existingSignature?.url && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg flex items-center gap-4">
          <img
            src={existingSignature.url}
            alt={`${signerName} signature on file`}
            className="h-12 bg-white rounded border border-gray-200 px-2"
          />
          <div className="text-sm text-blue-700">
            <p className="font-medium">Signature on file</p>
            <p className="text-xs text-blue-500">
              Saved{" "}
              {existingSignature.created_at
                ? new Date(existingSignature.created_at).toLocaleString()
                : ""}
              {" — "}saving a new one below will replace it.
            </p>
          </div>
        </div>
      )}

      {/* Draw mode */}
      {mode === "draw" && (
        <div>
          <div className="border-2 border-dashed border-gray-300 rounded-lg overflow-hidden bg-gray-50">
            <canvas
              ref={canvasRef}
              className="w-full touch-none cursor-crosshair bg-white"
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={stopDraw}
              onMouseLeave={stopDraw}
              onTouchStart={startDraw}
              onTouchMove={draw}
              onTouchEnd={stopDraw}
            />
          </div>
          <div className="flex items-center justify-between mt-2">
            <p className="text-xs text-gray-500">
              Sign above using your mouse, trackpad, or finger.
            </p>
            <button
              type="button"
              onClick={clearCanvas}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-600"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Upload mode */}
      {mode === "upload" && (
        <div>
          <label className="flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg h-[180px] bg-gray-50 cursor-pointer hover:bg-gray-100 transition-colors">
            {uploadPreview ? (
              <img
                src={uploadPreview}
                alt="Signature preview"
                className="max-h-full max-w-full object-contain p-2"
              />
            ) : (
              <div className="text-center">
                <Upload className="h-6 w-6 text-gray-400 mx-auto mb-2" />
                <p className="text-sm text-gray-500">
                  Click to choose a signature image
                </p>
                <p className="text-xs text-gray-400 mt-1">PNG or JPG</p>
              </div>
            )}
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleFileChange}
            />
          </label>
          {uploadFile && (
            <button
              type="button"
              onClick={() => {
                setUploadFile(null);
                setUploadPreview(null);
              }}
              className="flex items-center gap-1 text-xs text-gray-500 hover:text-red-600 mt-2"
            >
              <Trash2 className="h-3.5 w-3.5" />
              Remove
            </button>
          )}
        </div>
      )}

      {error && (
        <p className="text-sm text-red-600 mt-3">{error}</p>
      )}

      <button
        type="button"
        onClick={handleSave}
        disabled={saving}
        className="mt-4 w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
      >
        {saving ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            Saving...
          </>
        ) : (
          <>
            <Check className="h-4 w-4" />
            Save Signature
          </>
        )}
      </button>
    </div>
  );
};

export default SignatureCapture;